import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Package,
  XCircle,
  Check,
  X,
  Plus,
  Search,
  Clock,
  TrendingDown,
  Calendar,
  FileText,
  Trash2,
  Eye,
  Filter,
  Loader2
} from 'lucide-react';
import { useProducts } from '../../context/ProductContext';
import lossTrackingService, {
  LossReason,
  LossStatus,
  LossReasonLabels,
  LossStatusLabels
} from '../../services/lossTrackingService';
import { fuzzyMatch } from '../../lib/fuzzySearch';

const LossManagement = () => {
  const { products } = useProducts();
  const [losses, setLosses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [reasonFilter, setReasonFilter] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLoss, setSelectedLoss] = useState(null);

  // Get status filter based on active tab
  const getStatusFilter = (tab) => {
    if (tab === 'all') return undefined;
    if (tab === 'pending') return LossStatus.PENDING;
    if (tab === 'approved') return LossStatus.APPROVED;
    return undefined;
  };

  // Fetch losses
  const fetchLosses = useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = getStatusFilter(activeTab);
      
      const result = await lossTrackingService.getAll({
        status: statusFilter,
        limit: 100
      });
      setLosses(result.data || result);
    } catch (err) {
      console.error('Failed to fetch losses:', err);
      setError('Failed to load loss records');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const data = await lossTrackingService.getSummary();
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, []);

  useEffect(() => {
    fetchLosses();
    fetchSummary();
  }, [fetchLosses, fetchSummary]);

  // Auto-refresh every 30 seconds for live data
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLosses();
      fetchSummary();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchLosses, fetchSummary]);

  // Filter losses
  const filteredLosses = losses.filter(loss => {
    const matchesSearch = fuzzyMatch(searchTerm, loss.product?.name || '') ||
      fuzzyMatch(searchTerm, loss.description || '');
    const matchesReason = reasonFilter === 'all' || loss.reason === reasonFilter;
    return matchesSearch && matchesReason;
  });

  // Stats
  const pendingCount = losses.filter(l => l.status === LossStatus.PENDING).length;
  const totalLossValue = summary?.totalLossValue || 0;
  const expiryLossCount = summary?.expiryRelatedLosses?.expired || 0;

  // Handle delete
  const handleDelete = async (id) => {
    if (!globalThis.confirm('Are you sure you want to delete this loss record?')) return;
    setLoading(true);
    try {
      await lossTrackingService.delete(id);
      setSuccess('Loss record deleted');
      fetchLosses();
      fetchSummary();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete loss record');
    } finally {
      setLoading(false);
    }
  };

  // Get reason color
  const getReasonColor = (reason) => {
    switch (reason) {
      case LossReason.EXPIRED:
      case LossReason.NEAR_EXPIRY:
        return 'bg-orange-100 text-orange-700';
      case LossReason.DAMAGED:
      case LossReason.SPOILED:
        return 'bg-red-100 text-red-700';
      case LossReason.THEFT:
        return 'bg-purple-100 text-purple-700';
      case LossReason.QUALITY_ISSUE:
      case LossReason.RECALL:
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case LossStatus.PENDING:
        return 'bg-yellow-100 text-yellow-700';
      case LossStatus.APPROVED:
        return 'bg-green-100 text-green-700';
      case LossStatus.REJECTED:
        return 'bg-red-100 text-red-700';
      case LossStatus.WRITTEN_OFF:
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Loss Tracking</h1>
          <p className="text-gray-600 text-sm sm:text-base">Record and manage inventory losses</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center justify-center gap-2 bg-[#548B2E] hover:bg-[#3f6b20] text-white px-4 py-2 rounded-lg font-medium transition-colors w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          Record Loss
        </button>
      </div>

      {/* Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center gap-2">
          <XCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-yellow-500">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending Review</p>
              <p className="text-2xl font-bold text-gray-800">{pendingCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-red-500">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Loss Value</p>
              <p className="text-2xl font-bold text-gray-800">₹{totalLossValue.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-orange-500">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Expiry Losses</p>
              <p className="text-2xl font-bold text-gray-800">{expiryLossCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Records</p>
              <p className="text-2xl font-bold text-gray-800">{summary?.totalLossCount || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm">
        {/* Tabs */}
        <div className="border-b border-gray-100">
          <div className="flex flex-wrap">
            {[
              { id: 'pending', label: 'Pending' },
              { id: 'approved', label: 'Approved' },
              { id: 'all', label: 'All Records' },
              { id: 'analytics', label: 'Analytics' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 sm:px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-[#548B2E] border-b-2 border-[#548B2E]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.id === 'pending' && pendingCount > 0 && (
                  <span className="ml-2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Records View */}
          {activeTab !== 'analytics' && (
            <>
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by product or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  />
                </div>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value)}
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent appearance-none bg-white"
                  >
                    <option value="all">All Reasons</option>
                    {Object.entries(LossReasonLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Table */}
              {loading && (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading...</p>
                </div>
              )}
              {!loading && filteredLosses.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No loss records found</p>
                </div>
              )}
              {!loading && filteredLosses.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Qty</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Reason</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Value</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredLosses.map((loss) => (
                        <tr key={loss.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{loss.product?.name || `Product #${loss.productId}`}</p>
                                {loss.batchNumber && (
                                  <p className="text-xs text-gray-500">Batch: {loss.batchNumber}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-lg font-bold text-gray-800">{loss.quantity}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getReasonColor(loss.reason)}`}>
                              {LossReasonLabels[loss.reason] || loss.reason}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(loss.status)}`}>
                              {LossStatusLabels[loss.status] || loss.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-medium text-red-600">₹{Number(loss.totalLossValue || 0).toLocaleString()}</span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-500">
                            {new Date(loss.lossDate).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => { setSelectedLoss(loss); setShowDetailsModal(true); }}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {loss.status === LossStatus.PENDING && (
                                <button
                                  onClick={() => handleDelete(loss.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Analytics View */}
          {activeTab === 'analytics' && summary && (
            <div className="space-y-8">
              {/* Loss by Reason */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Loss by Reason</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {summary.byReason?.map((item) => (
                    <div key={item.reason} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getReasonColor(item.reason)}`}>
                          {LossReasonLabels[item.reason] || item.reason}
                        </span>
                        <span className="text-sm font-medium text-gray-500">{item.count} records</span>
                      </div>
                      <p className="text-xl font-bold text-gray-800">{item.quantity} units</p>
                      <p className="text-sm text-red-600">₹{item.value.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Products with Losses */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Products with Losses</h3>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Records</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Quantity</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Total Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {summary.byProduct?.slice(0, 5).map((item) => (
                        <tr key={item.productId}>
                          <td className="px-4 py-3 font-medium text-gray-800">{item.productName}</td>
                          <td className="px-4 py-3 text-center text-gray-600">{item.count}</td>
                          <td className="px-4 py-3 text-center text-gray-600">{item.quantity}</td>
                          <td className="px-4 py-3 text-right text-red-600 font-medium">₹{item.value.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Expiry Related Summary */}
              {summary.expiryRelatedLosses && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Expiry-Related Losses (Shelf Life)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <p className="text-sm text-orange-600 mb-1">Expired Products</p>
                      <p className="text-2xl font-bold text-orange-700">{summary.expiryRelatedLosses.expired}</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <p className="text-sm text-yellow-600 mb-1">Near Expiry Disposal</p>
                      <p className="text-2xl font-bold text-yellow-700">{summary.expiryRelatedLosses.nearExpiry}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <p className="text-sm text-red-600 mb-1">Total Quantity Lost</p>
                      <p className="text-2xl font-bold text-red-700">{summary.expiryRelatedLosses.totalQuantity}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <p className="text-sm text-red-600 mb-1">Total Value Lost</p>
                      <p className="text-2xl font-bold text-red-700">₹{summary.expiryRelatedLosses.totalValue.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Loss Modal */}
      {showCreateModal && (
        <CreateLossModal
          products={products}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchLosses();
            fetchSummary();
            setSuccess('Loss record created successfully');
            setTimeout(() => setSuccess(null), 3000);
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedLoss && (
        <DetailsModal
          loss={selectedLoss}
          onClose={() => { setShowDetailsModal(false); setSelectedLoss(null); }}
        />
      )}
    </div>
  );
};

// Create Loss Modal Component
const CreateLossModal = ({ products, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    productId: '',
    quantity: 1,
    reason: LossReason.DAMAGED,
    lossDate: new Date().toISOString().split('T')[0],
    description: '',
    notes: '',
    unitCost: '',
    expiryDate: '',
    batchNumber: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.productId) {
      onError('Please select a product');
      return;
    }
    setLoading(true);
    try {
      const data = {
        ...formData,
        productId: Number(formData.productId),
        quantity: Number(formData.quantity),
        unitCost: formData.unitCost ? Number(formData.unitCost) : undefined,
        expiryDate: formData.expiryDate || undefined,
        batchNumber: formData.batchNumber || undefined
      };
      await lossTrackingService.create(data);
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to create loss record');
    } finally {
      setLoading(false);
    }
  };

  const selectedProduct = products.find(p => p.id === Number(formData.productId));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Record Inventory Loss</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Product Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
            <select
              value={formData.productId}
              onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              required
            >
              <option value="">Select a product</option>
              {products.filter(p => p.isAvailable).map(p => (
                <option key={p.id} value={p.id}>{p.name} (Stock: {p.stockQuantity})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
              <input
                type="number"
                min="1"
                max={selectedProduct?.stockQuantity || 999}
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                required
              />
            </div>

            {/* Loss Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Loss Date *</label>
              <input
                type="date"
                value={formData.lossDate}
                onChange={(e) => setFormData({ ...formData, lossDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
            <select
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              required
            >
              {Object.entries(LossReasonLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Shelf-life fields (shown for expiry reasons) */}
          {(formData.reason === LossReason.EXPIRED || formData.reason === LossReason.NEAR_EXPIRY) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Batch Number</label>
                <input
                  type="text"
                  value={formData.batchNumber}
                  onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                  placeholder="e.g., BATCH-2026-04"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Unit Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.unitCost}
              onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
              placeholder={selectedProduct ? `Default: ₹${selectedProduct.pricePerUnit || selectedProduct.price}` : 'Leave blank for product price'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what happened..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] font-medium disabled:opacity-50"
            >
              {loading ? 'Recording...' : 'Record Loss'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Details Modal Component
const DetailsModal = ({ loss, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Loss Record Details</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 uppercase">Product</label>
              <p className="font-medium text-gray-800">{loss.product?.name || `Product #${loss.productId}`}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase">Quantity Lost</label>
              <p className="font-medium text-gray-800">{loss.quantity} units</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase">Reason</label>
              <p className="font-medium text-gray-800">{LossReasonLabels[loss.reason]}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase">Status</label>
              <p className="font-medium text-gray-800">{LossStatusLabels[loss.status]}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase">Loss Date</label>
              <p className="font-medium text-gray-800">{new Date(loss.lossDate).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase">Total Value</label>
              <p className="font-medium text-red-600">₹{Number(loss.totalLossValue || 0).toLocaleString()}</p>
            </div>
            {loss.batchNumber && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Batch Number</label>
                <p className="font-medium text-gray-800">{loss.batchNumber}</p>
              </div>
            )}
            {loss.expiryDate && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Expiry Date</label>
                <p className="font-medium text-gray-800">{new Date(loss.expiryDate).toLocaleDateString()}</p>
              </div>
            )}
            {loss.daysUntilExpiry !== null && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Days Until Expiry</label>
                <p className={`font-medium ${loss.daysUntilExpiry < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                  {loss.daysUntilExpiry < 0 ? `${Math.abs(loss.daysUntilExpiry)} days past` : `${loss.daysUntilExpiry} days`}
                </p>
              </div>
            )}
          </div>
          {loss.description && (
            <div>
              <label className="text-xs text-gray-500 uppercase">Description</label>
              <p className="text-gray-700">{loss.description}</p>
            </div>
          )}
          {loss.notes && (
            <div>
              <label className="text-xs text-gray-500 uppercase">Notes</label>
              <p className="text-gray-700 whitespace-pre-wrap">{loss.notes}</p>
            </div>
          )}
          <div className="pt-4">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LossManagement;